import { useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import giftIconJson from '../../../assets/missions/gift-icon-sprite.json';
import giftIconSprite from '../../../assets/missions/gift-icon-sprite.png';
import { useSpriteSheet, type SpriteSheetAsset } from '../../../hooks/useSpriteSheet';
import { analytics } from '../../../services/analytics';
import type { BundleItem } from '../../../stores/rewards/types';
import { formatValueWithK } from '../../../utils/helpers';
import { BundleItemsRenderer } from '../../BundleItemsRenderer/BundleItemsRenderer';
import { CoinWithValue } from '../../CoinWithValue/CoinWithValue';
import { SpriteAnimation } from '../../EventTriggerDialog/road/components/SpriteAnimation/SpriteAnimation';
import { DynamicTooltip } from '../../Tooltip/DynamicTooltip';
import { MISSIONS_TEST_ID_PREFIX } from '../constants';

import classes from './GiftTooltip.module.scss';

const GRAND_PRIZE_GIFT_ICON_SPRITE: SpriteSheetAsset = {
  imageSrc: giftIconSprite,
  dataSrc: JSON.stringify(giftIconJson),
  frameCount: 30,
  animationDuration: 600,
};

export type GiftTooltipProps = {
  scCoin: number;
  gcCoin: number;
  bundleItems?: BundleItem[] | null;
  playAnimation?: boolean;
};

export const GiftTooltip = ({ scCoin, gcCoin, bundleItems, playAnimation }: GiftTooltipProps) => {
  const { t } = useTranslation();
  // Div ref so it works for both the SpriteAnimation wrapper and the image wrapper (tooltip target).
  const iconRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const giftSpriteState = useSpriteSheet(GRAND_PRIZE_GIFT_ICON_SPRITE);

  const handleClick = () => {
    if (!isOpen) {
      // Reward values mirror what the tooltip displays, keyed per kind.
      const rewards = {
        ...(gcCoin > 0 && { GC: formatValueWithK(gcCoin, t) }),
        ...(scCoin > 0 && { SC: scCoin.toLocaleString() }),
        ...(bundleItems?.length && { BUNDLE_ITEMS: bundleItems.map((item) => item.bundleType) }),
      };
      void analytics.sendEvent('daily_missions', {
        event_category: 'daily_missions',
        event_name: 'open_milestone_reward',
        event_action: 'click',
        event_type: 'reward',
        event_value: rewards as any,
        page_url: window.location.pathname,
      });
    }
    setIsOpen(!isOpen);
  };

  return (
    <>
      <SpriteAnimation
        className={classes.grandPrizeGiftIcon}
        dataTestId={`${MISSIONS_TEST_ID_PREFIX}-progress-bar-gift-icon`}
        frameInterval={
          giftSpriteState.frameCount ? giftSpriteState.animationDuration / giftSpriteState.frameCount : undefined
        }
        idleFrame={'first'}
        isPlaying={!!playAnimation}
        refProp={iconRef}
        scale={0.8}
        sprite={giftSpriteState}
        onClick={handleClick}
      />
      <DynamicTooltip
        arrowAlign="dynamic"
        isOpen={isOpen}
        position="bottom"
        targetRef={iconRef}
        onClose={() => setIsOpen(false)}
      >
        <div className={classes.giftTooltip} data-testid={`${MISSIONS_TEST_ID_PREFIX}-progress-bar-gift-tooltip`}>
          <BundleItemsRenderer
            hidePlus
            bundleItems={bundleItems || []}
            className={classes.dailyMissionsGiftTooltipBundleItems}
            direction={'vertical-v2'}
            frbWidgetWidth={35}
            snappyGiftWidgetWidth={30}
          />
          {scCoin > 0 && (
            <CoinWithValue isPile coinSide="top" coinType="SC" valueClassName={classes.coinValue}>
              <span className={classes.coinValue}>{scCoin.toLocaleString()}</span>
            </CoinWithValue>
          )}
          {gcCoin > 0 && (
            <CoinWithValue isPile coinSide="top" coinType="GC" valueClassName={classes.coinValue}>
              <span className={classes.coinValue}>{formatValueWithK(gcCoin, t)}</span>
            </CoinWithValue>
          )}
        </div>
      </DynamicTooltip>
    </>
  );
};
